import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import QuickView from './display-system/QuickView';
import { getImagePath } from '../helpers/common';
import ImageWrapper from './ImageWrapper';
import { logEvent } from '../new-lib/analytics';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import ImageAltDescription from '../helpers/images-alt';
import { EntityTypes } from '../new-lib/constants/common';
import { EntityDetailsTypes } from '../new-lib/types/entity';

interface NawyImageGalleryProps {
  images: string[];
  fixedImage: string;
  entity?: any;
  type: EntityTypes;
}
const NawyImageGallery = ({
  images,
  fixedImage,
  entity,
  type
}: NawyImageGalleryProps) => {
  const { t } = useTranslation('common');
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [slideOpen, setSlideOpen] = useState(1);
  const [openIndex, setOpenIndex] = useState<number>(0);
  const [openQuickView, setOpenQuickView] = useState(false);
  const [GalleryComponent, setGalleryComponent] = useState<any>(null);

  const { locale } = useRouter();

  useEffect(() => {
    if (openQuickView && !GalleryComponent) {
      const Gallery = dynamic(() => import('./Gallery'));
      setGalleryComponent(Gallery);
    }
    return () => {
      setGalleryComponent(null);
      document.documentElement.classList.remove('fslightbox-open');
    };
  }, [openQuickView]);

  const imageAltDescription = new ImageAltDescription(entity, type, locale);
  const altDescription = imageAltDescription.getAltDescription();

  return (
    <Container>
      <div className="imageContainer">
        {images.slice(0, 6).map((image, index: any) => (
          <div
            key={index}
            className={`image-container ${
              selectedImage === index ? 'full-width-animation' : ''
            }`}
            onMouseEnter={() => {
              setSlideOpen(index + 1);
              setSelectedImage(index);
            }}
            onMouseLeave={() => setSelectedImage(0)}
            onClick={() => {
              logEvent.click('gallery_click', 'gallery', 'other');

              setOpenQuickView(true);
              setOpenIndex(index);
            }}
          >
            <ImageWrapper
              fill
              alt={altDescription}
              src={image}
              objectFit="cover"
              withSkeleton
              priority
              imageSize="default"
              sizes="100vw"
            />
            <div className="image-overlay" />
          </div>
        ))}
      </div>
      <div
        className="fixedImage"
        onClick={() => {
          logEvent.click('gallery_click', 'gallery', 'other');
          setOpenQuickView(true);
        }}
      >
        <ImageWrapper
          src={getImagePath(fixedImage)}
          alt="fixed-image"
          fill
          objectFit="cover"
          priority
          withSkeleton
          imageSize="default"
          sizes="100vw"
        />

        <ImageWrapper
          src={'/assets/icons/common/gallery.svg'}
          alt="image icon"
          width={55}
          height={50}
          className="gallery"
        />
        <div className="image-overlay" />
      </div>

      <QuickView isOpen={openQuickView} setIsOpen={setOpenQuickView}>
        {GalleryComponent && (
          <GalleryComponent
            show={openQuickView}
            slideNumber={openIndex}
            sources={images}
          />
        )}
      </QuickView>
    </Container>
  );
};
const Container = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  border-radius: ${props => props.theme.borderRadius};
  .image-overlay {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    background-color: rgba(30, 65, 100, 0.15);
    z-index: 11;
  }
  .imageContainer {
    width: 95%;
    height: 100%;
    display: flex;
    overflow: hidden;
    display: flex;
    gap: 0px 8px;
    .image-container {
      position: relative;
      transition: all 0.5s ease-in-out;
      width: 20%;
      height: 100%;
      float: left;
      overflow: hidden;
      overflow: hidden;
      cursor: pointer;
      img {
        filter: grayscale(100%) !important;
      }
    }

    .full-width-animation {
      width: 100%;
      .image-overlay {
        background: unset !important;
      }
      img {
        filter: inherit !important;
      }
    }

    @keyframes full-width {
      0% {
        width: 20%;
      }
      100% {
        width: 100%;
      }
    }
  }
  .fixedImage {
    width: 5%;
    height: 100%;
    position: relative;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-inline-start: 8px;
    img {
      filter: grayscale(100%) !important;
    }
    .gallery {
      z-index: 15;
    }
  }
`;
export default NawyImageGallery;
